import React from 'react'
import { useHistory } from 'react-router-dom'
import { FormTypes } from '@oneblink/types'
import { submissionService, autoSaveService } from '@oneblink/apps-react'
import { toast } from 'react-toastify'
import useQuery from './useQuery'
import useIsDraftInApprovals from './useIsDraftInApprovals'
import useAutoSaveKey from './useAutoSaveKey'
import { saveDraft } from '../lib/drafts'

export default function useSaveDraft({
  formDefinition,
  preFillData,
}: {
  formDefinition: FormTypes.Form
  preFillData: Record<string, unknown> | undefined
}): [
  (newDraftSubmission: submissionService.NewDraftSubmission) => unknown,
  boolean,
] {
  const [isSavingDraft, setIsSavingDraft] = React.useState<boolean>(false)
  const history = useHistory()
  const { trackingCode: qsTrackingCode } = useQuery()
  const existingTrackingCode = qsTrackingCode as string
  const [, fetchIsDraftInApprovals] = useIsDraftInApprovals()
  const autoSaveKey = useAutoSaveKey(preFillData)
  const onSaveDraft = React.useCallback(
    async (draftSubmission: submissionService.NewDraftSubmission) => {
      setIsSavingDraft(true)

      let inApprovals
      try {
        const { submission, definition } = draftSubmission

        if (existingTrackingCode) {
          inApprovals = await fetchIsDraftInApprovals(existingTrackingCode)
          if (inApprovals) {
            throw new Error('Draft has already been submitted')
          }
        }

        const trackingCode = await saveDraft(
          draftSubmission,
          existingTrackingCode as string,
        )

        await autoSaveService.deleteAutoSaveData(definition.id, autoSaveKey)

        let email
        if (submission.email) {
          email = submission.email
        } else if (submission.commonContactDetails) {
          const { email: submissionEmail } =
            submission.commonContactDetails as Record<string, unknown>
          email = submissionEmail
        }

        history.push({
          pathname: `/draft/${trackingCode}`,
          search: `?formId=${formDefinition.id}`,
          state: { email },
        })
      } catch (e) {
        console.error(e)
        toast.error(
          inApprovals
            ? 'Draft has already been submitted'
            : 'There was an error saving draft',
          { position: 'bottom-right', hideProgressBar: true },
        )
      } finally {
        setIsSavingDraft(false)
      }
    },
    [
      formDefinition,
      history,
      autoSaveKey,
      existingTrackingCode,
      fetchIsDraftInApprovals,
    ],
  )

  return [onSaveDraft, isSavingDraft]
}
